import React, { useEffect, useMemo, useState } from "react";
import adminPosApi from "../services/adminPosApi";
import adminApiClient from "../services/adminApiClient";

export default function AdminPosShiftsPage() {
  const [status, setStatus] = useState({ type: "", msg: "" });
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [terminalId, setTerminalId] = useState("");
  const [terminals, setTerminals] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [selectedShiftId, setSelectedShiftId] = useState(null);
  const [summary, setSummary] = useState(null);

  const [openForm, setOpenForm] = useState({ openingNote: "" });
  const [closeNote, setCloseNote] = useState("");

  const branchOptions = useMemo(() => branches || [], [branches]);

  async function loadBranches() {
    const res = await adminApiClient.get("/api/inventory/branches", { params: { limit: 200, offset: 0 } });
    if (!res.data?.ok) throw new Error("branches_load_failed");
    setBranches(res.data.branches || []);
  }

  async function loadTerminals() {
    if (!branchId) {
      setTerminals([]);
      return;
    }
    const res = await adminPosApi.listTerminals({ branchId: Number(branchId), limit: 200, offset: 0 });
    if (!res.ok) throw new Error(res.error || "terminals_load_failed");
    setTerminals(res.terminals || []);
  }

  async function loadShifts() {
    setStatus({ type: "", msg: "" });
    try {
      const params = { limit: 100, offset: 0 };
      if (branchId) params.branchId = Number(branchId);
      if (terminalId) params.terminalId = Number(terminalId);
      const res = await adminPosApi.listShifts(params);
      if (!res.ok) throw new Error(res.error || "shifts_load_failed");
      setShifts(res.shifts || []);
    } catch (e) {
      setStatus({ type: "error", msg: "Unable to load shifts." });
    }
  }

  async function loadSummary(shiftId) {
    try {
      const res = await adminPosApi.getShiftSummary(shiftId);
      if (!res.ok) throw new Error(res.error || "summary_failed");
      setSummary(res.summary || null);
    } catch {
      setSummary(null);
    }
  }

  useEffect(() => {
    loadBranches().catch(() => setStatus({ type: "error", msg: "Unable to load branches." }));
  }, []);

  useEffect(() => {
    loadTerminals()
      .then(() => {
        setTerminalId("");
      })
      .catch(() => setStatus({ type: "error", msg: "Unable to load terminals." }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  useEffect(() => {
    loadShifts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, terminalId]);

  async function onOpenShift(e) {
    e.preventDefault();
    setStatus({ type: "", msg: "" });

    if (!branchId || !terminalId) {
      setStatus({ type: "error", msg: "Branch and terminal are required to open a shift." });
      return;
    }

    try {
      const res = await adminPosApi.openShift({
        branchId: Number(branchId),
        terminalId: Number(terminalId),
        openingNote: openForm.openingNote || null,
      });
      if (!res.ok) throw new Error(res.error || "open_failed");
      setStatus({ type: "success", msg: "Shift opened." });
      setOpenForm({ openingNote: "" });
      await loadShifts();
    } catch (e) {
      setStatus({ type: "error", msg: "Unable to open shift. Ensure there is no existing open shift for this terminal." });
    }
  }

  async function onCloseShift(shiftId) {
    setStatus({ type: "", msg: "" });
    try {
      const res = await adminPosApi.closeShift(shiftId, { closingNote: closeNote || null });
      if (!res.ok) throw new Error(res.error || "close_failed");
      setStatus({ type: "success", msg: "Shift closed." });
      setCloseNote("");
      await loadShifts();
      if (selectedShiftId === shiftId) {
        await loadSummary(shiftId);
      }
    } catch {
      setStatus({ type: "error", msg: "Unable to close shift." });
    }
  }

  return (
    <div className="ucx-admin-page">
      <div className="ucx-admin-header">
        <div>
          <h1 className="ucx-admin-title">POS Shifts</h1>
          <div className="ucx-admin-subtitle">Open/close shifts per terminal (per-branch)</div>
        </div>
      </div>

      <div className="ucx-admin-panel">
        <div className="ucx-admin-panel-title">Open Shift</div>
        <form className="ucx-admin-form" onSubmit={onOpenShift}>
          <div className="ucx-admin-form-grid ucx-admin-form-grid-3">
            <div className="ucx-admin-field">
              <label>Branch</label>
              <select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                <option value="">Select branch</option>
                {branchOptions.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} (#{b.id})
                  </option>
                ))}
              </select>
            </div>

            <div className="ucx-admin-field">
              <label>Terminal</label>
              <select value={terminalId} onChange={(e) => setTerminalId(e.target.value)} disabled={!branchId}>
                <option value="">Select terminal</option>
                {terminals.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} (#{t.id})
                  </option>
                ))}
              </select>
            </div>

            <div className="ucx-admin-field">
              <label>Opening Note</label>
              <input
                value={openForm.openingNote}
                onChange={(e) => setOpenForm({ openingNote: e.target.value })}
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="ucx-admin-form-actions">
            <button className="ucx-btn" type="submit">
              Open Shift
            </button>
          </div>
        </form>

        <div className="ucx-admin-form-grid ucx-admin-form-grid-2" style={{ marginTop: "0.75rem" }}>
          <div className="ucx-admin-field">
            <label>Close Note (used when closing)</label>
            <input value={closeNote} onChange={(e) => setCloseNote(e.target.value)} placeholder="Optional" />
          </div>
          <div className="ucx-admin-field">
            <label>Status</label>
            <input value="Ready" readOnly />
          </div>
        </div>

        {status.type === "error" ? <div className="ucx-admin-status-error">{status.msg}</div> : null}
        {status.type === "success" ? <div className="ucx-admin-status-success">{status.msg}</div> : null}
      </div>

      <div className="ucx-admin-table-wrapper">
        <table className="ucx-admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Branch</th>
              <th>Terminal</th>
              <th>Status</th>
              <th>Opened</th>
              <th>Closed</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {shifts.length === 0 ? (
              <tr>
                <td colSpan={7} className="ucx-admin-empty">
                  No shifts found.
                </td>
              </tr>
            ) : (
              shifts.map((s) => (
                <tr
                  key={s.id}
                  onClick={() => {
                    setSelectedShiftId(s.id);
                    loadSummary(s.id);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <td>{s.id}</td>
                  <td>{s.branch_id}</td>
                  <td>{s.terminal_id}</td>
                  <td>{s.status}</td>
                  <td>{s.opened_at ? new Date(s.opened_at).toLocaleString() : "-"}</td>
                  <td>{s.closed_at ? new Date(s.closed_at).toLocaleString() : "-"}</td>
                  <td>
                    {s.status === "open" ? (
                      <button
                        type="button"
                        className="ucx-btn ucx-btn-outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          onCloseShift(s.id);
                        }}
                      >
                        Close
                      </button>
                    ) : (
                      <span className="ucx-admin-subtitle">—</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selectedShiftId ? (
        <div className="ucx-admin-panel" style={{ marginTop: "1rem" }}>
          <div className="ucx-admin-panel-title">Shift Summary (#{selectedShiftId})</div>
          {summary ? (
            <div className="ucx-admin-subtitle">
              Sales: {summary.salesCount ?? 0} • Gross: {summary.grossTotal ?? 0} • Tax: {summary.taxTotal ?? 0}
            </div>
          ) : (
            <div className="ucx-admin-subtitle">No summary available.</div>
          )}
        </div>
      ) : null}
    </div>
  );
}
